import { StagesEnum } from "@repo/api";
import { type IconType, clipboardPencilIcon, getIconByName } from "@repo/skapa";
import { type Settings, type StageAction } from "@repo/ui";

interface StageActionItem extends Omit<StageAction, "icon"> {
  icon: IconType;
  action: StagesActionEnum;
}

export enum StagesActionEnum {
  OPEN_DETAILS_MODAL = "Open details modal",
  OPEN_BOOKING_MODAL = "Open booking modal",
  SCROLL_TO_UPCOMING_SECTION = "Scroll to upcoming section",
  OPEN_ANALYSIS_MODAL = " Open analysis modal",
  NO_ACTION = "No action",
  SCROLL_TO_DRAWINGS_SECTION = "Scroll to drawings section",
  SCROLL_TO_LEFT_SECTION = "Scroll to left section",
  SCROLL_TO_RECORD_ACTIVITY_SECTION = "Scroll to record an activity section",
  OPEN_CONSENT_MODAL = "Open consent modal",
  SCROLL_TO_ORDERS_SECTION = "Scroll to orders section",
  SCROLL_TO_FILES_SECTION = "Scroll to files section",
  SCROLL_TO_NOTES_SECTION = "Scroll to notes section",
  SCROLL_TO_HISTORY_SECTION = "Scroll to history section",
  OPEN_TASK_MODAL = "Open task modal",
}

export enum STAGE_ACTIONS_SECTION_IDS {
  UPCOMING_SECTION = "project-upcoming-section",
  LEFT_SECTION = "project-left-section",
  RECORD_ACTIVITY_SECTION = "project-record-activity-section",
  DRAWINGS_SECTION = "project-drawings-section",
  CONSENT_SECTION = "project-consent-section",
  ORDERS_SECTION = "orders-section",
  FILES_SECTION = "files-section",
  NOTES_SECTION = "project-notes-section",
  HISTORY_SECTION = "project-history-section",
}

// Mapping from action_key to action type
const ACTION_CONFIG: Record<string, { action: StagesActionEnum }> = {
  ASSIGN_PROJECT: {
    action: StagesActionEnum.OPEN_DETAILS_MODAL,
  },
  ASSIGN_PROJECT_TO_KIC: {
    action: StagesActionEnum.OPEN_DETAILS_MODAL,
  },
  VALIDATE_CUSTOMER: {
    action: StagesActionEnum.OPEN_CONSENT_MODAL,
  },
  CHECK_NEED_ANALYSIS: {
    action: StagesActionEnum.OPEN_ANALYSIS_MODAL,
  },
  FINALIZE_NEED_ANALYSIS: {
    action: StagesActionEnum.OPEN_ANALYSIS_MODAL,
  },
  PREPARE_FOR_APPOINTMENT: {
    action: StagesActionEnum.NO_ACTION,
  },
  BOOK_FOLLOW_UP_APPOINTMENT: {
    action: StagesActionEnum.OPEN_BOOKING_MODAL,
  },
  ADD_DRAWING: {
    action: StagesActionEnum.SCROLL_TO_DRAWINGS_SECTION,
  },
  VERIFY_DRAWING: {
    action: StagesActionEnum.SCROLL_TO_DRAWINGS_SECTION,
  },
  ADD_ORDER: {
    action: StagesActionEnum.SCROLL_TO_ORDERS_SECTION,
  },
  ADD_NOTES: {
    action: StagesActionEnum.SCROLL_TO_NOTES_SECTION,
  },
  VALIDATE_NOTES: {
    action: StagesActionEnum.SCROLL_TO_HISTORY_SECTION,
  },
  VERIFY_ORDER_STATUS: {
    action: StagesActionEnum.SCROLL_TO_ORDERS_SECTION,
  },
  PRE_CALL_TASK: {
    action: StagesActionEnum.SCROLL_TO_UPCOMING_SECTION,
  },
  FOLLOW_UP_CALL_TASK: {
    action: StagesActionEnum.OPEN_TASK_MODAL,
  },
  POST_ORDER_DELIVERY_TASK: {
    action: StagesActionEnum.OPEN_TASK_MODAL,
  },
};

// Helper function to get stages array from settings
export const getStagesFromSettings = (
  projectStages?: Settings["project_stages"]
): StagesEnum[] => {
  if (!projectStages?.stages) {
    return [];
  }
  return projectStages.stages
    .map((stage) => {
      const stageEnum = StagesEnum[stage.stage_key as keyof typeof StagesEnum];
      if (!stageEnum) {
        // eslint-disable-next-line no-console
        console.warn(`Unknown stage key: ${stage.stage_key}`);
      }
      return stageEnum;
    })
    .filter((stage): stage is StagesEnum => stage !== undefined);
};

export const getStageActionsFromSettings = (
  projectStages?: Settings["project_stages"]
): Record<StagesEnum, StageActionItem[]> => {
  if (!projectStages?.stages || !projectStages?.actions) {
    return {} as Record<StagesEnum, StageActionItem[]>;
  }

  const actionsMap = new Map(
    projectStages.actions.map((action) => [action.action_key, action])
  );

  const stageActions: Record<StagesEnum, StageActionItem[]> = {} as Record<
    StagesEnum,
    StageActionItem[]
  >;

  projectStages.stages.forEach((stage) => {
    const stageEnum = StagesEnum[stage.stage_key as keyof typeof StagesEnum];

    if (!stageEnum) {
      // eslint-disable-next-line no-console
      console.warn(`Unknown stage key in actions: ${stage.stage_key}`);
      return;
    }

    stageActions[stageEnum] = stage.actions
      .map((action) => {
        const actionDetails = actionsMap.get(action.action_key);
        const actionConfig = ACTION_CONFIG[action.action_key];

        if (!actionDetails || !actionConfig) {
          return null;
        }

        // Get icon from settings, fallback to default
        const icon = getIconByName(actionDetails.icon) || clipboardPencilIcon;

        return {
          action_key: actionDetails.action_key,
          name: actionDetails.name,
          icon,
          action: actionConfig.action,
          ...(actionDetails.description && {
            description: actionDetails.description,
          }),
        };
      })
      .filter((action): action is StageActionItem => action !== null);
  });

  return stageActions;
};
